import React, { useCallback, useEffect, useRef, useState } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import {
  SearchPanelWidgetProps,
  SearchPanelWidgetState,
  SearchPanelProduct,
  SearchIconStyle,
} from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { useEditMode } from "../../../../utils/useEditMode";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { themeColor, ColorValue } from "../../../../types/color";
import { TOP_BAR_HEIGHT } from "../../../../dev/ThemeManager/styles";

// ============================================================================
// Font weight mapping
// ============================================================================

const fontWeightMap: Record<string, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// ============================================================================
// SVG icons
// ============================================================================

const SEARCH_ICON_OPTIONS: { label: string; value: SearchIconStyle }[] = [
  { label: "Classic", value: "magnifying-glass" },
  { label: "Alternate", value: "magnifying-glass-alt" },
  { label: "Bold", value: "magnifying-glass-bold" },
  { label: "Slim", value: "magnifying-glass-slim" },
  { label: "Dot", value: "magnifying-glass-dot" },
];

interface SearchIconProps {
  variant: SearchIconStyle;
  size: number;
  color: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
}

const SearchIcon: React.FC<SearchIconProps> = ({
  variant = "magnifying-glass",
  size,
  color,
  strokeWidth = 2,
  style,
}) => {
  const base: React.SVGProps<SVGSVGElement> = {
    xmlns: "http://www.w3.org/2000/svg",
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    style: { flexShrink: 0, ...style },
  };

  switch (variant) {
    case "magnifying-glass-alt":
      return (
        <svg {...base}>
          <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
          <path d="M21 21l-6-6" />
        </svg>
      );

    case "magnifying-glass-bold":
      return (
        <svg {...base} strokeWidth={strokeWidth + 0.75}>
          <circle cx="11" cy="11" r="7" />
          <line x1="16.5" y1="16.5" x2="21" y2="21" />
        </svg>
      );

    case "magnifying-glass-slim":
      return (
        <svg {...base} strokeWidth={Math.max(1, strokeWidth - 0.5)}>
          <circle cx="10.5" cy="10.5" r="8.5" />
          <line x1="21" y1="21" x2="17" y2="17" />
        </svg>
      );

    case "magnifying-glass-dot":
      return (
        <svg {...base}>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <circle cx="11" cy="11" r="1.5" fill={color} stroke="none" />
        </svg>
      );

    case "magnifying-glass":
    default:
      return (
        <svg {...base}>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      );
  }
};

const CloseIcon: React.FC<{ size: number; color: string }> = ({
  size,
  color,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, cursor: "pointer" }}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ============================================================================
// Default state — values derived from Figma designs
// ============================================================================

const defaultState: SearchPanelWidgetState = {
  // Trigger icon
  triggerIconStyle: "magnifying-glass",
  triggerIconThemeIcon: "default",
  triggerIconSize: 20,
  triggerIconColor: themeColor("textPrimary"),
  triggerIconStrokeWidth: 2,

  // Panel
  panelBgColor: themeColor("background"),

  // Search bar
  searchBarBgColor: themeColor("background"),
  searchBarBorderRadius: 30,
  searchBarMaxWidth: 800,
  searchBarHeight: 56,
  searchBarHeightMobile: 40,
  searchBarPlaceholder: "Search or ask concierge",
  searchBarFontSize: 14,
  searchBarFontSizeMobile: 12,
  searchBarTextColor: themeColor("textPrimary"),
  searchBarPlaceholderColor: themeColor("textPrimary"),
  searchBarIconSize: 24,
  searchBarIconSizeMobile: 16,

  // Ask concierge button
  askButtonBgColor: themeColor("textPrimary"),
  askButtonTextColor: themeColor("secondary"),
  askButtonBorderRadius: 100,
  askButtonFontSize: 14,
  askButtonFontSizeMobile: 10,
  askButtonText: "Ask concierge",
  askButtonPaddingX: 16,
  askButtonPaddingY: 4,

  // Close button
  closeIconSize: 24,
  closeIconSizeMobile: 20,
  closeIconColor: themeColor("textPrimary"),

  // Section headers
  sectionHeaderFontSize: 16,
  sectionHeaderFontSizeMobile: 14,
  sectionHeaderFontWeight: "semibold",
  sectionHeaderColor: themeColor("textPrimary"),

  // Body / question text
  bodyFontSize: 14,
  bodyColor: themeColor("textPrimary"),
  bodyLineHeight: 24,

  // Products
  productImageSize: 72,
  productImageSizeMobile: 56,
  productNameFontSize: 14,
  productPriceFontSize: 14,
  productPriceFontWeight: "semibold",
  productColumns: 2,

  // Trending searches
  trendingFontSize: 14,
  trendingColor: themeColor("textPrimary"),
  trendingLineHeight: 24,

  // Empty state
  noResultsText: "No search results found. Please try again.",
  noResultsWithSuggestionsText:
    "No search results found . Here's some ideas to get you started.",
  noSuggestionsText: "No suggestions found",
  noProductsText: "No products found",
  noSearchesText: "No searches found",

  // Spacing
  sectionGap: 32,
  contentPaddingX: 43,
  contentPaddingXMobile: 20,
  contentPaddingTop: 24,

  // Mobile dividers
  showMobileDividers: true,
  dividerColor: themeColor("background"),

  // Font
  fontFamily: "inherit",
};

// ============================================================================
// Edit fields
// ============================================================================

// Fields shown when editing the trigger icon (closed state)
const triggerEditFields: EditField[] = [
  {
    key: "triggerIconThemeIcon",
    label: "Custom Icon",
    type: "icon",
    group: "Trigger Icon",
  },
  {
    key: "triggerIconStyle",
    label: "Built-in Style",
    type: "options",
    options: SEARCH_ICON_OPTIONS,
    group: "Trigger Icon",
    condition: {
      field: "triggerIconThemeIcon",
      value: "default",
      operator: "equals",
    },
  },
  {
    key: "triggerIconSize",
    label: "Icon Size",
    type: "number",
    min: 12,
    max: 48,
    step: 2,
    group: "Trigger Icon",
  },
  {
    key: "triggerIconColor",
    label: "Icon Color",
    type: "color",
    group: "Trigger Icon",
    condition: {
      field: "triggerIconThemeIcon",
      value: "default",
      operator: "equals",
    },
  },
  {
    key: "triggerIconStrokeWidth",
    label: "Stroke Width",
    type: "number",
    min: 1,
    max: 4,
    step: 0.5,
    group: "Trigger Icon",
    condition: {
      field: "triggerIconThemeIcon",
      value: "default",
      operator: "equals",
    },
  },
];

// ---- Per-component edit field groups ----

const searchBarEditFields: EditField[] = [
  {
    key: "searchBarBgColor",
    label: "Background",
    type: "color",
    group: "Search Bar",
  },
  {
    key: "searchBarBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 50,
    step: 2,
    group: "Search Bar",
  },
  {
    key: "searchBarMaxWidth",
    label: "Max Width",
    type: "number",
    min: 400,
    max: 1200,
    step: 50,
    group: "Search Bar",
  },
  {
    key: "searchBarHeight",
    label: "Height (Desktop)",
    type: "number",
    min: 36,
    max: 72,
    step: 4,
    group: "Search Bar",
  },
  {
    key: "searchBarHeightMobile",
    label: "Height (Mobile)",
    type: "number",
    min: 32,
    max: 56,
    step: 4,
    group: "Search Bar",
  },
  {
    key: "searchBarPlaceholder",
    label: "Placeholder Text",
    type: "text",
    group: "Search Bar",
  },
  {
    key: "searchBarFontSize",
    label: "Font Size (Desktop)",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
    group: "Search Bar",
  },
  {
    key: "searchBarTextColor",
    label: "Text Color",
    type: "color",
    group: "Search Bar",
  },
  {
    key: "searchBarPlaceholderColor",
    label: "Placeholder Color",
    type: "color",
    group: "Search Bar",
  },
  {
    key: "askButtonBgColor",
    label: "Button Background",
    type: "color",
    group: "Ask Button",
  },
  {
    key: "askButtonTextColor",
    label: "Button Text Color",
    type: "color",
    group: "Ask Button",
  },
  {
    key: "askButtonBorderRadius",
    label: "Button Radius",
    type: "number",
    min: 0,
    max: 100,
    step: 4,
    group: "Ask Button",
  },
  {
    key: "askButtonText",
    label: "Button Text",
    type: "text",
    group: "Ask Button",
  },
  {
    key: "closeIconSize",
    label: "Close Icon Size",
    type: "number",
    min: 16,
    max: 32,
    step: 2,
    group: "Close Button",
  },
  {
    key: "closeIconColor",
    label: "Close Icon Color",
    type: "color",
    group: "Close Button",
  },
];

const questionsEditFields: EditField[] = [
  {
    key: "sectionHeaderFontSize",
    label: "Header Font Size",
    type: "number",
    min: 12,
    max: 24,
    step: 1,
    group: "Section Header",
  },
  {
    key: "sectionHeaderFontWeight",
    label: "Header Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
    group: "Section Header",
  },
  {
    key: "sectionHeaderColor",
    label: "Header Color",
    type: "color",
    group: "Section Header",
  },
  {
    key: "bodyFontSize",
    label: "Body Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
    group: "Body Text",
  },
  { key: "bodyColor", label: "Body Color", type: "color", group: "Body Text" },
];

const productsEditFields: EditField[] = [
  {
    key: "sectionHeaderFontSize",
    label: "Header Font Size",
    type: "number",
    min: 12,
    max: 24,
    step: 1,
    group: "Section Header",
  },
  {
    key: "sectionHeaderColor",
    label: "Header Color",
    type: "color",
    group: "Section Header",
  },
  {
    key: "productImageSize",
    label: "Image Size (Desktop)",
    type: "number",
    min: 48,
    max: 96,
    step: 8,
    group: "Products",
  },
  {
    key: "productImageSizeMobile",
    label: "Image Size (Mobile)",
    type: "number",
    min: 40,
    max: 72,
    step: 8,
    group: "Products",
  },
  {
    key: "productColumns",
    label: "Columns (Desktop)",
    type: "number",
    min: 1,
    max: 3,
    step: 1,
    group: "Products",
  },
  {
    key: "productNameFontSize",
    label: "Name Font Size",
    type: "number",
    min: 10,
    max: 18,
    step: 1,
    group: "Products",
  },
  {
    key: "productPriceFontSize",
    label: "Price Font Size",
    type: "number",
    min: 10,
    max: 18,
    step: 1,
    group: "Products",
  },
  {
    key: "productPriceFontWeight",
    label: "Price Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
    group: "Products",
  },
];

const trendingEditFields: EditField[] = [
  {
    key: "sectionHeaderFontSize",
    label: "Header Font Size",
    type: "number",
    min: 12,
    max: 24,
    step: 1,
    group: "Section Header",
  },
  {
    key: "sectionHeaderColor",
    label: "Header Color",
    type: "color",
    group: "Section Header",
  },
  {
    key: "trendingFontSize",
    label: "Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
    group: "Trending",
  },
  { key: "trendingColor", label: "Color", type: "color", group: "Trending" },
];

// Panel-level fields (background, spacing, dividers)
const panelEditFields: EditField[] = [
  {
    key: "panelBgColor",
    label: "Panel Background",
    type: "color",
    group: "Panel",
  },
  { key: "fontFamily", label: "Font Family", type: "font", group: "Panel" },
  {
    key: "sectionGap",
    label: "Section Gap",
    type: "number",
    min: 16,
    max: 64,
    step: 4,
    group: "Spacing",
  },
  {
    key: "contentPaddingX",
    label: "Padding X (Desktop)",
    type: "number",
    min: 16,
    max: 80,
    step: 4,
    group: "Spacing",
  },
  {
    key: "contentPaddingXMobile",
    label: "Padding X (Mobile)",
    type: "number",
    min: 12,
    max: 40,
    step: 4,
    group: "Spacing",
  },
  {
    key: "noResultsText",
    label: "No Results Text",
    type: "text",
    group: "Empty States",
  },
  {
    key: "noResultsWithSuggestionsText",
    label: "No Results (with suggestions)",
    type: "text",
    group: "Empty States",
  },
  {
    key: "showMobileDividers",
    label: "Show Mobile Dividers",
    type: "toggle",
    group: "Dividers",
  },
  {
    key: "dividerColor",
    label: "Divider Color",
    type: "color",
    group: "Dividers",
  },
];

// Combined fields for useWidgetEditable (needs all fields for auto-transformations)
const allEditFields: EditField[] = [
  ...triggerEditFields,
  ...searchBarEditFields,
  ...questionsEditFields,
  ...productsEditFields,
  ...trendingEditFields,
  ...panelEditFields,
];

// ============================================================================
// Loading skeleton
// ============================================================================

const SkeletonLine: React.FC<{ width: string }> = ({ width }) => (
  <div
    style={{
      width,
      height: "18px",
      borderRadius: "3px",
      backgroundColor: "#e5e7eb",
      animation: "searchPanelPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
    }}
  />
);

const SkeletonProductRow: React.FC<{ imageSize: number }> = ({ imageSize }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
    <div
      style={{
        width: imageSize,
        height: imageSize,
        borderRadius: "8px",
        backgroundColor: "#e5e7eb",
        flexShrink: 0,
        animation: "searchPanelPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
      }}
    />
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <SkeletonLine width="140px" />
      <SkeletonLine width="60px" />
    </div>
  </div>
);

// ============================================================================
// Sub-components
// ============================================================================

/** Individual product row */
const ProductRow: React.FC<{
  product: SearchPanelProduct;
  imageSize: number;
  nameSize: number;
  priceSize: number;
  priceWeight: number;
  textColor: string;
  nameMaxWidth?: number;
  onClick?: () => void;
}> = ({
  product,
  imageSize,
  nameSize,
  priceSize,
  priceWeight,
  textColor,
  nameMaxWidth,
  onClick,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      cursor: onClick ? "pointer" : undefined,
    }}
    onClick={onClick}
  >
    <img
      src={product.imageUrl}
      alt={product.name}
      style={{
        width: imageSize,
        height: imageSize,
        objectFit: "contain",
        flexShrink: 0,
        borderRadius: "4px",
      }}
    />
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        minWidth: 0,
        maxWidth: nameMaxWidth,
      }}
    >
      <span
        style={{
          fontSize: `${nameSize}px`,
          fontWeight: 400,
          lineHeight: "20px",
          color: textColor,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {product.name}
      </span>
      <span
        style={{
          fontSize: `${priceSize}px`,
          fontWeight: priceWeight,
          lineHeight: "20px",
          color: textColor,
        }}
      >
        {product.price}
      </span>
    </div>
  </div>
);

// ============================================================================
// Main component
// ============================================================================

export const SearchPanelWidget: React.FC<SearchPanelWidgetProps> = ({
  widgetId = "search-panel",
  displayName = "Search Panel",
  initialState = {},

  conciergeQuestions,
  popularProducts,
  trendingSearches,

  isOpen,
  searchQuery,
  isLoading = false,
  hasSearched = false,

  onOpen,
  onClose,
  onSearchChange,
  onSearchSubmit,
  onAskConcierge,
  onQuestionClick,
  onProductClick,
  onTrendingClick,

  brandLogo,
}) => {
  const {
    state,
    isLoading: isThemeLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<SearchPanelWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    allEditFields
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const isEditMode = useEditMode();

  // Responsive breakpoint detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Auto-focus search input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        onSearchSubmit(searchQuery);
      }
    },
    [searchQuery, onSearchSubmit]
  );

  // Show a default-styled icon immediately while theme loads (avoids 1-2s delay)
  if (isThemeLoading && !isOpen) {
    return (
      <div
        onClick={onOpen}
        style={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <SearchIcon
          variant={defaultState.triggerIconStyle}
          size={defaultState.triggerIconSize}
          color={
            typeof defaultState.triggerIconColor === "string"
              ? defaultState.triggerIconColor
              : "#4f4b47"
          }
          strokeWidth={defaultState.triggerIconStrokeWidth}
          style={{ cursor: "pointer" }}
        />
      </div>
    );
  }

  if (isThemeLoading) return null;

  // Resolve trigger icon colors (needed for both open and closed states)
  const triggerColor = resolveColor(state.triggerIconColor);

  // Resolve theme icon URL (if a custom uploaded icon is selected)
  const themeIconKey = state.triggerIconThemeIcon;
  const themeIconUrl =
    themeIconKey && themeIconKey !== "default"
      ? themeIcons[themeIconKey]?.url
      : undefined;

  // When panel is closed, render the editable trigger icon
  if (!isOpen) {
    const triggerIcon = (
      <div
        onClick={onOpen}
        style={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        {themeIconUrl ? (
          <img
            src={themeIconUrl}
            alt="Search"
            style={{
              width: state.triggerIconSize,
              height: state.triggerIconSize,
              objectFit: "contain",
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
        ) : (
          <SearchIcon
            variant={state.triggerIconStyle}
            size={state.triggerIconSize}
            color={triggerColor}
            strokeWidth={state.triggerIconStrokeWidth}
            style={{ cursor: "pointer" }}
          />
        )}
      </div>
    );

    return (
      <WidgetWrapper
        widgetId={widgetId}
        displayName="Search Icon"
        editFields={triggerEditFields}
        editValues={state}
        onEditChange={handleEditChange}
        onSave={canSave ? handleSave : undefined}
        isSaving={isSaving}
        themeColors={themeColors}
        themeFonts={themeFonts}
        themeIcons={themeIcons}
        wrapperStyle={{ display: "inline-flex", width: "auto" }}
        iconOffset={{ top: -16, left: 24 }}
      >
        {triggerIcon}
      </WidgetWrapper>
    );
  }

  // Resolve colors
  const panelBg = resolveColor(state.panelBgColor);
  const searchBarBg = resolveColor(state.searchBarBgColor);
  const searchBarText = resolveColor(state.searchBarTextColor);
  const searchBarPlaceholder = resolveColor(state.searchBarPlaceholderColor);
  const askBtnBg = resolveColor(state.askButtonBgColor);
  const askBtnText = resolveColor(state.askButtonTextColor);
  const closeColor = resolveColor(state.closeIconColor);
  const headerColor = resolveColor(state.sectionHeaderColor);
  const bodyColor = resolveColor(state.bodyColor);
  const trendingColor = resolveColor(state.trendingColor);
  const dividerColor = resolveColor(state.dividerColor);

  const headerWeight = fontWeightMap[state.sectionHeaderFontWeight] || 600;
  const priceWeight = fontWeightMap[state.productPriceFontWeight] || 600;

  // Responsive values
  const barHeight = isMobile
    ? state.searchBarHeightMobile
    : state.searchBarHeight;
  const barFontSize = isMobile
    ? state.searchBarFontSizeMobile
    : state.searchBarFontSize;
  const iconSize = isMobile
    ? state.searchBarIconSizeMobile
    : state.searchBarIconSize;
  const closeSize = isMobile ? state.closeIconSizeMobile : state.closeIconSize;
  const headerFontSize = isMobile
    ? state.sectionHeaderFontSizeMobile
    : state.sectionHeaderFontSize;
  const imgSize = isMobile
    ? state.productImageSizeMobile
    : state.productImageSize;
  const padX = isMobile ? state.contentPaddingXMobile : state.contentPaddingX;
  const askBtnFontSize = isMobile
    ? state.askButtonFontSizeMobile
    : state.askButtonFontSize;

  // Determine content state
  const hasQuestions = conciergeQuestions.length > 0;
  const hasProducts = popularProducts.length > 0;
  const hasTrending = trendingSearches.length > 0;
  const hasAnyContent = hasQuestions || hasProducts || hasTrending;
  const showNoResultsBanner = hasSearched && !isLoading;
  const showContentSections = !hasSearched || hasAnyContent;

  // ---- Render helper: section header ----
  const renderSectionHeader = (text: string) => (
    <h3
      style={{
        margin: 0,
        fontSize: `${headerFontSize}px`,
        fontWeight: headerWeight,
        lineHeight: isMobile ? "24px" : "32px",
        color: headerColor,
      }}
    >
      {text}
    </h3>
  );

  // ---- Render helper: concierge questions section ----
  const renderQuestionsSection = () => {
    if (isLoading) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {renderSectionHeader("Ask Concierge")}
          <SkeletonLine width="80%" />
          <SkeletonLine width="70%" />
          <SkeletonLine width="60%" />
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {renderSectionHeader("Ask Concierge")}
        {hasQuestions ? (
          conciergeQuestions.map((q, i) => (
            <p
              key={i}
              onClick={() => onQuestionClick(q)}
              style={{
                margin: 0,
                fontSize: `${state.bodyFontSize}px`,
                fontWeight: 400,
                lineHeight: `${state.bodyLineHeight}px`,
                color: bodyColor,
                cursor: "pointer",
              }}
            >
              {q}
            </p>
          ))
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: `${state.bodyFontSize}px`,
              fontWeight: 400,
              lineHeight: `${state.bodyLineHeight}px`,
              color: bodyColor,
              opacity: 0.7,
            }}
          >
            {state.noSuggestionsText}
          </p>
        )}
      </div>
    );
  };

  // ---- Render helper: products section ----
  const renderProductsSection = () => {
    if (isLoading) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {renderSectionHeader("Popular products")}
          <SkeletonProductRow imageSize={imgSize} />
          <SkeletonProductRow imageSize={imgSize} />
          <SkeletonProductRow imageSize={imgSize} />
        </div>
      );
    }

    // On desktop, split products into columns
    const columns = isMobile ? 1 : state.productColumns;
    const productChunks: SearchPanelProduct[][] = [];
    if (hasProducts) {
      const chunkSize = Math.ceil(popularProducts.length / columns);
      for (let i = 0; i < columns; i++) {
        productChunks.push(
          popularProducts.slice(i * chunkSize, (i + 1) * chunkSize)
        );
      }
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {renderSectionHeader("Popular products")}
        {hasProducts ? (
          <div
            style={{
              display: isMobile ? "flex" : "grid",
              gridTemplateColumns: isMobile
                ? undefined
                : `repeat(${columns}, 1fr)`,
              flexDirection: isMobile ? "column" : undefined,
              gap: isMobile ? "16px" : "24px",
            }}
          >
            {isMobile
              ? popularProducts.map((p, i) => (
                  <ProductRow
                    key={i}
                    product={p}
                    imageSize={imgSize}
                    nameSize={state.productNameFontSize}
                    priceSize={state.productPriceFontSize}
                    priceWeight={priceWeight}
                    textColor={bodyColor}
                    onClick={p.onClick ?? (() => onProductClick(p))}
                  />
                ))
              : productChunks.map((chunk, ci) => (
                  <div
                    key={ci}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    {chunk.map((p, i) => (
                      <ProductRow
                        key={i}
                        product={p}
                        imageSize={imgSize}
                        nameSize={state.productNameFontSize}
                        priceSize={state.productPriceFontSize}
                        priceWeight={priceWeight}
                        textColor={bodyColor}
                        nameMaxWidth={250}
                        onClick={p.onClick ?? (() => onProductClick(p))}
                      />
                    ))}
                  </div>
                ))}
          </div>
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: `${state.bodyFontSize}px`,
              fontWeight: 400,
              color: bodyColor,
              opacity: 0.7,
            }}
          >
            {state.noProductsText}
          </p>
        )}
      </div>
    );
  };

  // ---- Render helper: trending searches section ----
  const renderTrendingSection = () => {
    if (isLoading) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {renderSectionHeader("Trending searches")}
          <SkeletonLine width="75%" />
          <SkeletonLine width="65%" />
          <SkeletonLine width="80%" />
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {renderSectionHeader("Trending searches")}
        {hasTrending ? (
          trendingSearches.map((t, i) => (
            <p
              key={i}
              onClick={() => onTrendingClick(t)}
              style={{
                margin: 0,
                fontSize: `${state.trendingFontSize}px`,
                fontWeight: 400,
                lineHeight: `${state.trendingLineHeight}px`,
                color: trendingColor,
                cursor: "pointer",
              }}
            >
              {t}
            </p>
          ))
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: `${state.bodyFontSize}px`,
              fontWeight: 400,
              color: bodyColor,
              opacity: 0.7,
            }}
          >
            {state.noSearchesText}
          </p>
        )}
      </div>
    );
  };

  // ---- Mobile divider ----
  const renderDivider = () => {
    if (!isMobile || !state.showMobileDividers) return null;
    return (
      <div
        style={{
          height: "1px",
          backgroundColor: dividerColor,
          margin: `${state.sectionGap / 2}px 0`,
        }}
      />
    );
  };

  // ================================================================
  // PANEL LAYOUT
  // ================================================================

  const topOffset = isEditMode ? TOP_BAR_HEIGHT : 0;

  const widgetContent = (
    <>
      <style>{`
        @keyframes searchPanelPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes searchPanelSlideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes searchPanelFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* ---- Backdrop (click to close) ---- */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          top: topOffset,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483645,
          backgroundColor: isMobile ? "transparent" : "rgba(0, 0, 0, 0.15)",
          animation: "searchPanelFadeIn 0.25s ease-out",
        }}
      />

      {/* ---- Panel ---- */}
      <div
        style={{
          position: "fixed",
          top: topOffset,
          left: 0,
          right: 0,
          ...(isMobile ? { bottom: 0 } : {}),
          zIndex: 2147483646,
          backgroundColor: panelBg,
          display: "flex",
          flexDirection: "column",
          overflow: isMobile ? "auto" : "visible",
          fontFamily: state.fontFamilyCss || "inherit",
          animation: "searchPanelSlideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          ...(isMobile
            ? {}
            : {
                maxHeight: `calc(100vh - ${topOffset}px)`,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              }),
        }}
      >
        {/* ---- Header row ---- */}
        <WidgetWrapper
          widgetId={widgetId ? `${widgetId}__search-bar` : undefined}
          displayName="Search Bar"
          editFields={searchBarEditFields}
          editValues={state}
          onEditChange={handleEditChange}
          onSave={canSave ? handleSave : undefined}
          isSaving={isSaving}
          themeColors={themeColors}
          themeFonts={themeFonts}
          wrapperStyle={{ display: "flex", width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "12px" : "24px",
              padding: isMobile
                ? "24px 20px 0 20px"
                : `36px ${padX}px 0 ${padX}px`,
              flexShrink: 0,
              width: "100%",
            }}
          >
            {/* Brand logo (desktop: left side) */}
            {!isMobile && brandLogo && (
              <div style={{ flexShrink: 0 }}>{brandLogo}</div>
            )}

            {/* Search bar — centered via auto margins */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                backgroundColor: searchBarBg,
                borderRadius: `${state.searchBarBorderRadius}px`,
                maxWidth: `${state.searchBarMaxWidth}px`,
                margin: "0 auto",
                height: `${barHeight}px`,
                padding: isMobile ? "0 8px 0 12px" : "0 12px 0 16px",
                gap: isMobile ? "8px" : "12px",
              }}
            >
              {themeIconUrl ? (
                <img
                  src={themeIconUrl}
                  alt="Search"
                  style={{
                    width: iconSize,
                    height: iconSize,
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <SearchIcon
                  variant={state.triggerIconStyle}
                  size={iconSize}
                  color={searchBarText}
                />
              )}
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={state.searchBarPlaceholder}
                style={{
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  outline: "none",
                  fontSize: `${barFontSize}px`,
                  fontWeight: 400,
                  lineHeight: isMobile ? "16px" : "24px",
                  color: searchBarText,
                  fontFamily: "inherit",
                }}
              />
              {/* "Ask concierge" button */}
              <button
                onClick={() => onAskConcierge(searchQuery)}
                style={{
                  flexShrink: 0,
                  backgroundColor: askBtnBg,
                  color: askBtnText,
                  border: "none",
                  borderRadius: `${state.askButtonBorderRadius}px`,
                  padding: isMobile
                    ? "6px 8px"
                    : `${state.askButtonPaddingY}px ${state.askButtonPaddingX}px`,
                  fontSize: `${askBtnFontSize}px`,
                  fontWeight: 500,
                  lineHeight: isMobile ? "16px" : "32px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                }}
              >
                {state.askButtonText}
              </button>
            </div>

            {/* Close button */}
            <div
              onClick={onClose}
              style={{ flexShrink: 0, cursor: "pointer", padding: "4px" }}
              role="button"
              aria-label="Close search panel"
            >
              <CloseIcon size={closeSize} color={closeColor} />
            </div>
          </div>
        </WidgetWrapper>

        {/* ---- Content area ---- */}
        <div
          style={{
            ...(isMobile ? { flex: 1 } : {}),
            padding: `${state.contentPaddingTop}px ${padX}px ${padX}px`,
            overflow: isMobile ? "auto" : "auto",
          }}
        >
          {/* No-results banner */}
          {showNoResultsBanner && (
            <p
              style={{
                margin: `0 0 ${state.sectionGap}px`,
                fontSize: `${state.sectionHeaderFontSize}px`,
                fontWeight: headerWeight,
                lineHeight: "32px",
                color: headerColor,
                textAlign: hasAnyContent ? "center" : undefined,
              }}
            >
              {hasAnyContent
                ? state.noResultsWithSuggestionsText
                : state.noResultsText}
            </p>
          )}

          {/* Content sections */}
          {showContentSections && (
            <>
              {isMobile ? (
                /* ---- Mobile: stacked single column ---- */
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: `${state.sectionGap}px`,
                  }}
                >
                  <WidgetWrapper
                    widgetId={widgetId ? `${widgetId}__questions` : undefined}
                    displayName="Ask Concierge"
                    editFields={questionsEditFields}
                    editValues={state}
                    onEditChange={handleEditChange}
                    onSave={canSave ? handleSave : undefined}
                    isSaving={isSaving}
                    themeColors={themeColors}
                    themeFonts={themeFonts}
                  >
                    {renderQuestionsSection()}
                  </WidgetWrapper>
                  {renderDivider()}
                  <WidgetWrapper
                    widgetId={widgetId ? `${widgetId}__products` : undefined}
                    displayName="Popular Products"
                    editFields={productsEditFields}
                    editValues={state}
                    onEditChange={handleEditChange}
                    onSave={canSave ? handleSave : undefined}
                    isSaving={isSaving}
                    themeColors={themeColors}
                    themeFonts={themeFonts}
                  >
                    {renderProductsSection()}
                  </WidgetWrapper>
                  {renderDivider()}
                  <WidgetWrapper
                    widgetId={widgetId ? `${widgetId}__trending` : undefined}
                    displayName="Trending Searches"
                    editFields={trendingEditFields}
                    editValues={state}
                    onEditChange={handleEditChange}
                    onSave={canSave ? handleSave : undefined}
                    isSaving={isSaving}
                    themeColors={themeColors}
                    themeFonts={themeFonts}
                  >
                    {renderTrendingSection()}
                  </WidgetWrapper>
                </div>
              ) : (
                /* ---- Desktop: 3-column layout ---- */
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(200px, 1fr) minmax(350px, 2fr) minmax(200px, 1fr)",
                    gap: `${state.sectionGap}px`,
                  }}
                >
                  <WidgetWrapper
                    widgetId={widgetId ? `${widgetId}__questions` : undefined}
                    displayName="Ask Concierge"
                    editFields={questionsEditFields}
                    editValues={state}
                    onEditChange={handleEditChange}
                    onSave={canSave ? handleSave : undefined}
                    isSaving={isSaving}
                    themeColors={themeColors}
                    themeFonts={themeFonts}
                  >
                    {renderQuestionsSection()}
                  </WidgetWrapper>
                  <WidgetWrapper
                    widgetId={widgetId ? `${widgetId}__products` : undefined}
                    displayName="Popular Products"
                    editFields={productsEditFields}
                    editValues={state}
                    onEditChange={handleEditChange}
                    onSave={canSave ? handleSave : undefined}
                    isSaving={isSaving}
                    themeColors={themeColors}
                    themeFonts={themeFonts}
                  >
                    {renderProductsSection()}
                  </WidgetWrapper>
                  <WidgetWrapper
                    widgetId={widgetId ? `${widgetId}__trending` : undefined}
                    displayName="Trending Searches"
                    editFields={trendingEditFields}
                    editValues={state}
                    onEditChange={handleEditChange}
                    onSave={canSave ? handleSave : undefined}
                    isSaving={isSaving}
                    themeColors={themeColors}
                    themeFonts={themeFonts}
                  >
                    {renderTrendingSection()}
                  </WidgetWrapper>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={panelEditFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
